import React, { Component, cloneElement } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';

import { renderableType } from '../proptypes/prop-types';
import {MenuItem} from './menuItem';
import { buildClassNames, buildClassNameModifiers, px } from '../util/utils';
import './menu.scss';

const blockClassName = 'menu';
const listClassName = 'menu__list';
const listItemClassName = 'menu__listItem';
const labelIconClassName = 'menu__labelIcon';
const iconClassName = 'menu__icon';
const toggleClassName = 'menu__toggle';

const timeout = 250;

/** A component for displaying simple menu structures */
export class Menu extends Component {
  static propTypes = {
    /** @ignore */
    className: PropTypes.string,
    /** @ignore */
    children: renderableType,
    /** @ignore */
    isMenu: PropTypes.bool,
    /** Indicates that the `menu` represents the current page in the site hierarchy */
    current: PropTypes.bool,
    /** Indicates that the menu is disabled */
    disabled: PropTypes.bool,
    /** The icon to be rendered next to the label */
    icon: renderableType,
    /** The items to be rendered in the menu using the specified renderer */
    items: PropTypes.arrayOf(PropTypes.shape({
      /** The icon to be rendered next to the label */
      icon: renderableType,
      /** Text displayed when rendering the menu item */
      label: PropTypes.string,
      /** Text displayed on hover */
      title: PropTypes.string,
      /** Function executed when the menu item is clicked */
      onClick: PropTypes.func,
    })),
    /** The item in the nav that triggers the appearance of the sub items */
    label: renderableType.isRequired,
    /** The alignment of the menu in relation to the menu toggle */
    menuAlignment: PropTypes.oneOf(['left', 'right', 'full']),
    /** Text displayed on hover */
    title: renderableType,
  };
  static defaultProps = {
    isMenu: true,
    current: false,
    disabled: false,
    menuAlignment: 'left',
  };
  state = { open: false };

  componentDidMount() {
    this.toggleClickOutsideEvent(true);
  }

  componentWillUnmount() {
    this.toggleClickOutsideEvent(false);
  }

  handleClickOutside = (e) => {
    if (this.state.open) {
      if (!this.node || !this.node.contains(e.target)) {
        if (this.state.open) this.setState({ open: false });
      }
    }
  }

  toggleClickOutsideEvent(enabled) {
    if (enabled) {
      document.addEventListener('click', this.handleClickOutside);
    } else {
      document.removeEventListener('click', this.handleClickOutside);
    }
  }
  handleMenuItemClick = (e, onClick) => {
    if (onClick) {
      e.stopPropagation();
      const savedEvent = e;
      this.setState({ open: false }, () => onClick(savedEvent));
    }
  };
  handleMenuItemKeyDown = (e, onClick) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      const savedEvent = e;
      this.setState({ open: false }, () => onClick(savedEvent));
    }
  };
  handleToggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    this.setState({ open: !this.state.open });
  }
  handleBlur = (e) => {
    if (!this.node || !this.node.contains(e.target)) {
      e.stopPropagation();
      e.preventDefault();
      if (this.state.open) this.setState({ open: false });
    }
  }
  handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
      this.setState({ open: !this.state.open });
    }
    if (e.key === 'Tab') {
      if (!this.node || !this.node.contains(e.target)) {
        e.stopPropagation();
        this.setState({ open: false });
      }
    }
  };
  handlePropagation = (e) => {
    e.stopPropagation();
  };
  render() {
    const {
      children,
      className,
      isMenu,
      current,
      disabled,
      icon,
      items,
      label,
      menuAlignment,
      title,
    } = this.props;
    if (!isMenu) return null;
    const { open } = this.state;
    return (
      <div
        className={buildClassNames(
          className,
          buildClassNameModifiers(blockClassName, { open }),
          blockClassName,
        )}
        ref={(n) => { this.node = n; }}
      >
        <span
          className={buildClassNames(
            buildClassNameModifiers(toggleClassName, { open }, { current }, { disabled }),
            toggleClassName,
          )}
          role='button'
          tabIndex={disabled ? -1 : 0}
          title={title || label}
          onClick={disabled ? undefined : this.handleToggle}
          onKeyDown={disabled ? undefined : this.handleKeyDown}
        >
          <span>
            {icon && cloneElement(icon, { className: labelIconClassName }) }
            {label}
          </span>
          <FontAwesomeIcon
            className={buildClassNames(
              buildClassNameModifiers(iconClassName, { open }),
              iconClassName,
            )}
            icon={faCaretDown}
          />
        </span>
        <CSSTransition
          classNames={listClassName}
          in={open}
          timeout={timeout}
          mountOnEnter
          unmountOnExit
        >
          {() => {
            const menuStyle = {
              maxWidth: menuAlignment === 'left'
                ? `calc(100vw - ${px(this.menu ? this.menu.getBoundingClientRect().x : 0)} - 1rem)`
                : `calc(100vw - (100vw - ${px(this.menu ? this.menu.getBoundingClientRect().right : 0)}) - 1rem)`,
              minWidth: px(this.menu ? this.menu.getBoundingClientRect().width : 0),
            };
            return (
              <ul
                ref={(n) => { this.menu = n; }}
                role='menu'
                tabIndex={-1}
                className={buildClassNames(
                  buildClassNameModifiers(listClassName, menuAlignment),
                  listClassName,
                )}
                onClick={this.handlePropagation}
                onKeyDown={this.handlePropagation}
                style={menuStyle}
              >
                {children
                  ? React.Children.map(
                    children,
                    (child, index) => {
                      if (child.props && child.props.isDivider) {
                        return child;
                      }
                      return (
                        <li className={listItemClassName} key={child.key || `menuitem-${index}`}>
                          {child.props && child.props.isMenuItem
                            ? cloneElement(child, {
                              onClick: e => this.handleMenuItemClick(e, child.props.onClick),
                              onKeyDown: e => this.handleMenuItemKeyDown(e, child.props.onClick),
                            })
                            : (
                              <MenuItem
                                onClick={e => this.handleMenuItemClick(e, child.props.onClick)}
                                onKeyDown={e => this.handleMenuItemKeyDown(e, child.props.onClick)}
                              >
                                {child}
                              </MenuItem>
                            )
                          }
                        </li>
                      );
                    },
                  )
                  : items.map(item => (
                    <li className={listItemClassName} key={item.label}>
                      <MenuItem
                        title={item.title || item.label}
                        onClick={e => this.handleMenuItemClick(e, item.onClick)}
                        onKeyDown={e => this.handleMenuItemKeyDown(e, item.onClick)}
                      >
                        <span>
                          {item.icon && cloneElement(item.icon, { className: labelIconClassName }) }
                          {item.label}
                        </span>
                      </MenuItem>
                    </li>
                  ))
                }
              </ul>
            );
          }}
        </CSSTransition>
      </div>
    );
  }
}
